import { BuyersCertificate } from '@api/types';
import { CustomTextField, DropdownField, useBuyersCertificateCreateContext } from '@components';
import {
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Radio,
  RadioGroup,
  useTheme,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { forwardRef, useMemo } from 'react';
import { Controller } from 'react-hook-form';

import { useGetBuyersCertificateProductGroupsQuery } from '@api/buyersCert.api';
import { TextCodes } from '@api/types/translation/TextCodes';
import { t } from '@translations';

type BuyersCertificateFieldProps = {
  type?: string;
  label: string;
  name: keyof BuyersCertificate;
  options?: {
    label: string;
    value: string;
  }[];
  required?: boolean;
};

export const BuyersCertificateRadioGroup = ({
  name,
  label,
  options,
}: BuyersCertificateFieldProps) => {
  const { form } = useBuyersCertificateCreateContext();
  return (
    <Controller
      control={form.control}
      name={name}
      defaultValue={options?.[0].value}
      render={({ field }) => (
        <FormControl sx={{ mt: 5, width: '100%' }} required>
          <InputLabel
            id={`${name}-type`}
            sx={{
              maxWidth: '100%',
              overflow: {
                xs: 'hidden',
                sm: 'hidden',
              },
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
            }}
          >
            {label}
          </InputLabel>
          <RadioGroup {...field}>
            {options?.map((option) => (
              <FormControlLabel
                key={option.label}
                value={option.value}
                control={<Radio />}
                label={option.label}
                sx={{
                  width: 'fit-content',
                }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      )}
    />
  );
};

type BuyersCertificateProductSelectProps = {
  isEditEnabled: boolean;
};

type BuyersCertificateDateFieldProps = {
  label: string;
  name: keyof BuyersCertificate;
  required?: boolean;
};

export const BuyersCertificateDatePicker = forwardRef<
  HTMLDivElement,
  BuyersCertificateDateFieldProps
>(({ label, name, required }, ref) => {
  const { form } = useBuyersCertificateCreateContext();
  const getValue = () => {
    return form.watch(name) ? dayjs(form.watch(name)?.toString(), 'YYYY-MM-DD') : null;
  };
  const theme = useTheme();
  const isError = !!form.formState.errors[name];
  return (
    <Controller
      name={name}
      control={form.control}
      rules={{ required }}
      render={({ field }) => (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <FormControl sx={{ width: '100%' }} required={required} error={isError}>
            <DatePicker
              ref={ref}
              slotProps={{
                popper: {
                  sx: { zIndex: 1197 },
                },
                textField: { placeholder: 'DD/MM/YYYY', required: required },
              }}
              label={label}
              sx={{
                mt: 5,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: isError ? theme.palette.error.main : undefined,
                },
                '& .MuiFormLabel-root': {
                  color: isError ? theme.palette.error.main : undefined,
                },
              }}
              format='DD/MM/YYYY'
              value={getValue()}
              onChange={(newValue) => {
                field.onChange(dayjs(newValue)?.format('YYYY-MM-DD'));
              }}
              disableFuture
            />
          </FormControl>
        </LocalizationProvider>
      )}
    />
  );
});

export const BuyersCertificateProductSelect = ({
  isEditEnabled,
}: BuyersCertificateProductSelectProps) => {
  const { form, formValues } = useBuyersCertificateCreateContext();
  const selectedProductType = form.watch('productType') || formValues['productType'];
  const selectedModel = form.watch('productModel') || formValues['productModel'];
  const { currentData, isLoading } = useGetBuyersCertificateProductGroupsQuery();

  const productTypes = useMemo(() => {
    return currentData
      ?.map((item) => ({
        label: item.description,
        value: item.description,
      }))
      ?.concat({
        label: t(TextCodes.BuyersCertificate.Block.Other),
        value: t(TextCodes.BuyersCertificate.Block.Other),
      });
  }, [currentData]);

  const productModels = useMemo(
    () =>
      currentData
        ?.find((item) => item.description === selectedProductType)
        ?.productNames.map((item) => ({
          label: item,
          value: item,
        }))
        ?.concat({
          label: t(TextCodes.BuyersCertificate.Block.Other),
          value: t(TextCodes.BuyersCertificate.Block.Other),
        }) || [],
    [currentData, selectedProductType]
  );

  return (
    <>
      <Grid item xs={12} md={6} lg={5}>
        <Controller
          name='productType'
          control={form.control}
          rules={{ required: true }}
          render={({ field }) => (
            <FormControl sx={{ mt: 5, width: '100%' }} required>
              <DropdownField
                label={t(TextCodes.BuyersCertificate.Block.ProductType) + ' *'}
                {...field}
                getValue={(value) => {
                  field.onChange(value as string);
                  form.setValue('productModel', '');
                }}
                value={field.value || ''}
                options={productTypes || []}
                error={!!form.formState.errors.productType}
                isLoading={isLoading}
                disabled={isLoading}
                readOnly={!isEditEnabled}
              />
            </FormControl>
          )}
        />
      </Grid>
      <Grid item xs={12} md={6} lg={5}>
        <Controller
          name='productModel'
          control={form.control}
          rules={{ required: true }}
          render={({ field }) => (
            <FormControl sx={{ mt: 5, width: '100%' }} required>
              {selectedProductType === t(TextCodes.BuyersCertificate.Block.Other) ? (
                <CustomTextField
                  fullWidth
                  label={t(TextCodes.BuyersCertificate.Block.ProductModel) + ' *'}
                  variant='outlined'
                  error={!!form.formState.errors.productModel}
                  {...field}
                />
              ) : (
                <DropdownField
                  label={t(TextCodes.BuyersCertificate.Block.ProductModel) + ' *'}
                  {...field}
                  value={field.value || ''}
                  getValue={(value) => {
                    field.onChange(value as string);
                    form.setValue('productCustomModel', '');
                  }}
                  options={productModels || []}
                  error={!!form.formState.errors.productModel}
                  isLoading={isLoading}
                  disabled={isLoading}
                  readOnly={!isEditEnabled}
                />
              )}
            </FormControl>
          )}
        />
      </Grid>
      {selectedModel === t(TextCodes.BuyersCertificate.Block.Other) && (
        <Grid item xs={12} md={6} lg={5}>
          <Controller
            name='productCustomModel'
            control={form.control}
            rules={{ required: true }}
            render={({ field }) => (
              <FormControl sx={{ mt: 2, width: '100%' }} required>
                <CustomTextField
                  fullWidth
                  {...field}
                  label={t(TextCodes.BuyersCertificate.Block.ProductCustomModel) + ' *'}
                  variant='outlined'
                  error={!!form.formState.errors.productCustomModel}
                  value={field.value || ''}
                />
              </FormControl>
            )}
          />
        </Grid>
      )}
    </>
  );
};
